import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { Stack, TextField, Box, InputAdornment, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import pinFill from '@iconify/icons-eva/pin-fill';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { EventContext } from '../../../contexts/Event';
// services
import { updateEvent } from '../../../services/Event';

EventFormLocation.propTypes = {
  event: PropTypes.object,
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

export default function EventFormLocation({ event, handleNext, handleBack, edit }) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setEvent } = useContext(EventContext);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const eventLocationValues = {
    location: {
      address: {
        place: event?.location?.address?.place || '',
        formatted: event?.location?.address?.formatted || ''
      },
      map: {
        url: event?.location?.map?.url || ''
      }
    }
  };

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const EventLocationSchema = Yup.object().shape({
    location: Yup.object({
      address: Yup.object({
        place: Yup.string(),
        formatted: Yup.string()
      }),
      map: Yup.object({
        url: Yup.string().url('Link to Google Maps must be a valid URL')
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: eventLocationValues,
    validationSchema: EventLocationSchema,
    onSubmit: async (values) => {
      setIsSubmitted(false);
      setIsLoading(true);

      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };
        const payload = {
          location: {
            address: {
              place: values.location.address.place,
              formatted: values.location.address.formatted
            },
            map: {
              url: values.location.map.url
            }
          }
        };

        await updateEvent({ headers, payload, id: event._id });
        setEvent((event) => ({ ...event, ...payload }));

        setIsLoading(false);

        if (!edit) {
          handleNext();
        } else {
          setIsSubmitted(true);
        }
      } catch (err) {
        setIsLoading(false);
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const { errors, touched, handleSubmit, getFieldProps } = formik;

  return (
    <Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Full Address (optional)"
              {...getFieldProps('location.address.formatted')}
              error={Boolean(
                touched.location?.address?.formatted && errors.location?.address?.formatted
              )}
              helperText={
                touched.location?.address?.formatted && errors.location?.address?.formatted
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon icon={pinFill} width="24" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Place (optional)"
              {...getFieldProps('location.address.place')}
              error={Boolean(touched.location?.address?.place && errors.location?.address?.place)}
              helperText={touched.location?.address?.place && errors.location?.address?.place}
              placeholder="i.e. Altice Arena - Lisbon"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon icon={pinFill} width="24" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Link to Google Maps (optional)"
              {...getFieldProps('location.map.url')}
              error={Boolean(touched.location?.map?.url && errors.location?.map?.url)}
              helperText={touched.location?.map?.url && errors.location?.map?.url}
            />

            <Box>
              {!edit && (
                <>
                  <Button size="large" onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button size="large" variant="outlined" onClick={handleNext} sx={{ mr: 1 }}>
                    Next
                  </Button>
                </>
              )}
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isLoading}
                endIcon={isSubmitted && <Icon icon={checkmarkCircleFill} />}
              >
                Save
              </LoadingButton>
            </Box>
          </Stack>
        </Form>
      </FormikProvider>
    </Box>
  );
}
